import type {
  BaseServiceOptions,
  Pagination,
  TBaseCatalogs,
  TRequestCatalog,
} from '@/core/types'
import {
  type TLog,
  moduleServiceHandlers,
  catalogServiceHandlers,
  type TLogs,
  serviceConfig,
} from '../../..'
import { defaultPagination, useNotification, useServiceHandlers } from '@/core'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { AxiosError } from 'axios'
import { toValue } from 'vue'
import { useScrapStore } from '../../store/details'
import { useCatalogStore } from '@/core/store/catalogs'
import { TMill } from '@/modules/mills'

const { model } = serviceConfig

export const useModuleQuery = (options: BaseServiceOptions<Pagination>) => {
  const { currentPage, handlers, enableQuery, filters } = options
  const store = useScrapStore()
  const { successWithActionNotification } = useNotification()
  const storeCatalog = useCatalogStore()

  const { errorHttpNotification } = useNotification()
  const serviceHandlers = useServiceHandlers<TLog>({
    model,
    currentPage: toValue(currentPage) ?? 1,
    filterParams: filters?.loadRecords ?? '',
  })

  const editRecord = useMutation<TLog, AxiosError, TLog>(
    (payload: TLog) => moduleServiceHandlers.editRecord(payload, payload.id),
    {
      onSuccess: () => {
        serviceHandlers.invalidateQueries()
        successWithActionNotification('UPDATED', 'scrap')
      },
      onError: (error) => errorHttpNotification(error),
    },
  )

  const loadRecords = useQuery<TLogs, AxiosError>(
    [model, currentPage, filters?.loadRecords],
    () =>
      moduleServiceHandlers.loadRecords({
        dynamicFilters: filters?.loadRecords,
        page: currentPage,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.loadRecords,
      onError: (error) => errorHttpNotification(error),
      onSuccess: (data) => {
        store.setScrap(data.data)
        if (data.meta) {
          handlers?.onDataChange?.(data.meta ?? defaultPagination)
        }
      },
    },
  )

  const requestCatalog = useMutation<
    TBaseCatalogs,
    AxiosError,
    TRequestCatalog
  >(
    (payload: Record<any, any>) =>
      catalogServiceHandlers.categoriesCatalog.requestCatalog(payload),
    {
      onSuccess: (payload) => {
        storeCatalog.setCatalogs(payload)
      },
      onError: (error) => errorHttpNotification(error),
    },
  )

  const millsCatalog = useQuery<TMill[], AxiosError>({
    queryKey: [serviceConfig.catalogs.mills, filters?.millsCatalog],
    queryFn: () =>
      catalogServiceHandlers.millsCatalog.loadCatalog({
        dynamicFilters: filters?.millsCatalog,
      }),
    refetchOnWindowFocus: false,
    enabled: enableQuery?.millsCatalog,
  })

  return {
    loadRecords,
    editRecord,
    requestCatalog,
    millsCatalog,
  }
}
